import { useState, useEffect } from 'react';
import { Calendar, ChevronDown, Filter } from 'lucide-react';

interface TimeFilterProps {
  onFilterChange: (startDate: Date, endDate: Date) => void;
  seasons?: { id: string; name: string }[];
  venues?: { id: string; name: string }[];
  onSeasonChange?: (seasonId: string) => void;
  onVenueChange?: (venueId: string) => void;
}

type QuickOption = '7days' | 'all' | 'custom';

export default function TimeFilter({ 
  onFilterChange, 
  seasons = [], 
  venues = [],
  onSeasonChange,
  onVenueChange,
}: TimeFilterProps) {
  const [quickOption, setQuickOption] = useState<QuickOption>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);

  const getDateRange = (option: QuickOption): { start: Date; end: Date } => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    switch (option) {
      case '7days':
        const start7 = new Date(end);
        start7.setDate(start7.getDate() - 7);
        return { start: start7, end };
      case 'all':
      default:
        return { start: new Date(2020, 0, 1), end };
    }
  };

  useEffect(() => {
    if (quickOption !== 'custom') {
      const { start, end } = getDateRange(quickOption);
      onFilterChange(start, end);
    }
  }, [quickOption]);

  useEffect(() => {
    if (quickOption === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      end.setHours(23, 59, 59);
      onFilterChange(start, end);
    }
  }, [customStartDate, customEndDate, quickOption]);

  const handleSeasonSelect = (seasonId: string) => {
    setSelectedSeason(seasonId);
    setShowSeasonDropdown(false);
    onSeasonChange?.(seasonId);
  };

  const handleVenueSelect = (venueId: string) => {
    setSelectedVenue(venueId);
    setShowVenueDropdown(false);
    onVenueChange?.(venueId);
  };

  const options: { value: QuickOption | 'custom'; label: string }[] = [
    { value: '7days', label: '近 7 天' },
    { value: 'all', label: '全部时间' },
  ];

  const uniqueSeasons = seasons.length > 0 ? seasons : [];
  const uniqueVenues = venues.length > 0 ? venues : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Calendar className="w-4 h-4 text-avalon-gold" />
          <span className="text-avalon-text text-sm whitespace-nowrap">时间:</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setQuickOption(opt.value as QuickOption);
                setShowCustomPicker(false);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                quickOption === opt.value
                  ? 'bg-avalon-purple text-white'
                  : 'bg-avalon-card text-avalon-text hover:text-white hover:bg-avalon-purple/50'
              }`}
            >
              {opt.label}
            </button>
          ))}

          <button
            onClick={() => {
              setQuickOption('custom' as QuickOption);
              setShowCustomPicker(true);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${
              quickOption === 'custom'
                ? 'bg-avalon-purple text-white'
                : 'bg-avalon-card text-avalon-text hover:text-white hover:bg-avalon-purple/50'
            }`}
          >
            自定义
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>

        {showCustomPicker && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-2 py-1.5 bg-avalon-card border border-avalon-purple/30 rounded-lg text-white text-sm focus:outline-none focus:border-avalon-purple"
            />
            <span className="text-avalon-text">至</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-2 py-1.5 bg-avalon-card border border-avalon-purple/30 rounded-lg text-white text-sm focus:outline-none focus:border-avalon-purple"
            />
          </div>
        )}
      </div>

      {(onSeasonChange || onVenueChange) && (
        <div className="flex flex-row items-start sm:items-center gap-3">
          {onVenueChange && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-avalon-gold" />
              <span className="text-avalon-text text-sm">道场:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setShowVenueDropdown(!showVenueDropdown);
                    setShowSeasonDropdown(false);
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors flex items-center gap-2 min-w-[100px]"
                >
                  {selectedVenue === 'all' ? '全部' : uniqueVenues.find(v => v.id === selectedVenue)?.name || selectedVenue}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showVenueDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-avalon-card border border-avalon-purple/30 rounded-lg shadow-lg z-50 min-w-[120px]">
                    <button
                      onClick={() => handleVenueSelect('all')}
                      className={`w-full px-3 py-2 text-sm text-left hover:bg-avalon-purple/50 transition-colors ${
                        selectedVenue === 'all' ? 'bg-avalon-purple text-white' : 'text-white'
                      }`}
                    >
                      全部道场
                    </button>
                    {uniqueVenues.map((venue) => (
                      <button
                        key={venue.id}
                        onClick={() => handleVenueSelect(venue.id)}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-avalon-purple/50 transition-colors ${
                          selectedVenue === venue.id ? 'bg-avalon-purple text-white' : 'text-white'
                        }`}
                      >
                        {venue.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {onSeasonChange && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-avalon-gold" />
              <span className="text-avalon-text text-sm">赛季:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setShowSeasonDropdown(!showSeasonDropdown);
                    setShowVenueDropdown(false);
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors flex items-center gap-2 min-w-[100px]"
                >
                  {selectedSeason === 'all' ? '全部' : uniqueSeasons.find(s => s.id === selectedSeason)?.name || selectedSeason}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showSeasonDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-avalon-card border border-avalon-purple/30 rounded-lg shadow-lg z-50 min-w-[120px]">
                    <button
                      onClick={() => handleSeasonSelect('all')}
                      className={`w-full px-3 py-2 text-sm text-left hover:bg-avalon-purple/50 transition-colors ${
                        selectedSeason === 'all' ? 'bg-avalon-purple text-white' : 'text-white'
                      }`}
                    >
                      全部赛季
                    </button>
                    {uniqueSeasons.map((season) => (
                      <button
                        key={season.id}
                        onClick={() => handleSeasonSelect(season.id)}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-avalon-purple/50 transition-colors ${
                          selectedSeason === season.id ? 'bg-avalon-purple text-white' : 'text-white'
                        }`}
                      >
                        {season.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
